import type { Flag } from '@/state/flags/flag-types';
import type { FlagManager } from '@/state/flags/useFlag';
import { Lock } from 'lucide-react';
import { useFlagGoal } from '@/state/flags/useFlagGoal';
import { PrimaryObjectivePicker } from '../../Experiments/Details/ExperimentSettings/Objectives/PrimaryObjectivePicker';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagGoalProps = { manager: FlagManager; flag: Flag };

const COPY = FLAG_COPY.goal;

/**
 * The flag's primary goal, for code and editor flags alike: the event the guardrail compares
 * On and Off on. The experiments' primary objective picker, without the secondaries and saved
 * sets of their Objectives card: a flag reports no results, so only the guardrail's goal matters.
 */
export const FlagGoal = ({ manager, flag }: FlagGoalProps) => {
  const goal = useFlagGoal({ manager, flag, copy: COPY });

  return (
    <FlagSection title={COPY.title} help={COPY.help} description={COPY.description} tourAnchor="flag-goal">
      <div className="flex flex-col gap-2">
        {!goal.hasEvents && goal.value === null ? (
          <p className="supporting m-0">{COPY.noEvents}</p>
        ) : (
          <PrimaryObjectivePicker
            value={goal.value}
            defaultEvent={goal.defaultEvent}
            groups={goal.groups}
            onChange={(next) => void goal.save(next)}
            disabled={goal.isLocked}
          />
        )}
        {goal.isFixedLive && (
          <p className="supporting m-0 flex items-center gap-2">
            <Lock className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
            {COPY.locked}
          </p>
        )}
      </div>
    </FlagSection>
  );
};
